import * as React from "react";
import { Plus, Trash2, ShieldAlert, ShieldCheck, Clock, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import DetailDrawer from "@/components/DetailDrawer";
import { EmptyState } from "@/components/feel/EmptyState";
import { toast } from "sonner";

/**
 * RecordsPanel — reusable list of expiring records.
 *
 * Modes:
 *   * scope="company"      — HR "Records" tab: shows every record the caller
 *                             can view. Filter by status + subject_type.
 *   * scope="member"       — Member drawer Records tab: shows a single
 *                             member's records only. Add/edit/delete
 *                             disabled unless canEdit.
 *   * scope="site"         — Site's records (reserved; not exposed yet).
 */
export default function RecordsPanel({
  companyId, canEdit = false,
  scope = "company", subjectType = null, subjectId = null,
  hideAdd = false, testidPrefix = "records",
}) {
  const [rows, setRows] = React.useState(null);
  const [counts, setCounts] = React.useState({ valid: 0, expiring_soon: 0, lapsed: 0 });
  const [filter, setFilter] = React.useState("");
  const [subjectFilter, setSubjectFilter] = React.useState("");
  const [drawer, setDrawer] = React.useState(null);   // record | 'new'
  const [members, setMembers] = React.useState([]);
  const [sites, setSites] = React.useState([]);

  const load = React.useCallback(async () => {
    const params = {};
    if (scope === "member") { params.subject_type = "member"; params.subject_id = subjectId; }
    else if (scope === "site") { params.subject_type = "site"; params.subject_id = subjectId; }
    if (filter) params.status = filter;
    if (scope === "company" && subjectFilter) params.subject_type = subjectFilter;
    try {
      const { data } = await api.get(`/companies/${companyId}/records`, { params });
      setRows(data.records || []);
      setCounts(data.counts || { valid: 0, expiring_soon: 0, lapsed: 0 });
    } catch (e) {
      setRows([]);
    }
  }, [companyId, scope, subjectId, filter, subjectFilter]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const on = (e) => { const t = e.detail?.type;
      if (t && t.startsWith("record.")) load();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  // Fetch members / sites once for the create drawer picker
  React.useEffect(() => {
    if (scope !== "company") return;
    api.get(`/companies/${companyId}/members`)
       .then(({ data }) => setMembers(data.members || []))
       .catch(() => setMembers([]));
    api.get(`/companies/${companyId}/sites`)
       .then(({ data }) => setSites(data.sites || []))
       .catch(() => setSites([]));
  }, [companyId, scope]);

  async function del(rid) {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/companies/${companyId}/records/${rid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  const empty = rows && rows.length === 0;

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-panel`}>
      {/* Counts strip */}
      <div className="grid grid-cols-3 gap-2">
        <StatChip label="Valid" value={counts.valid} tone="emerald" testid={`${testidPrefix}-count-valid`} />
        <StatChip label="Expiring" value={counts.expiring_soon} tone="amber" testid={`${testidPrefix}-count-expiring`} />
        <StatChip label="Lapsed" value={counts.lapsed} tone="coral" testid={`${testidPrefix}-count-lapsed`} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <select className="field-input !h-8 !w-40 !text-[12px]" value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid={`${testidPrefix}-filter-status`}>
          <option value="">All statuses</option>
          <option value="valid">Valid</option>
          <option value="expiring_soon">Expiring</option>
          <option value="lapsed">Lapsed</option>
        </select>
        {scope === "company" && (
          <select className="field-input !h-8 !w-40 !text-[12px]" value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                  data-testid={`${testidPrefix}-filter-subject`}>
            <option value="">All subjects</option>
            <option value="member">Members</option>
            <option value="site">Sites</option>
          </select>
        )}
        <div className="flex-1" />
        {canEdit && !hideAdd && (
          <button className="btn-violet !h-8" onClick={() => setDrawer("new")}
                  data-testid={`${testidPrefix}-new`}>
            <Plus size={13}/> Add record
          </button>
        )}
      </div>

      <div className="card-panel overflow-hidden">
        <div className="table-scroll"><table className="fable-table" data-testid={`${testidPrefix}-table`}>
          <thead>
            <tr>
              <th style={{width: "22%"}}>Subject</th>
              <th style={{width: "22%"}}>Record</th>
              <th style={{width: "18%"}}>Expires</th>
              <th style={{width: "14%"}}>Status</th>
              <th style={{width: "14%"}}>Blocking</th>
              {canEdit && <th style={{width: "6%"}}/>}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id} data-clickable="true" onClick={() => setDrawer(r)}
                  data-testid={`${testidPrefix}-row-${r.id}`}>
                <td>
                  <div className="text-[12.5px] font-medium text-brand-ink">
                    {r.subject_name || r.subject_id?.slice(0, 8)}
                  </div>
                  <div className="text-[10px] text-brand-muted mono-num">{r.subject_type}</div>
                </td>
                <td>
                  <div className="text-[12.5px] font-medium text-brand-ink">{r.type_label}</div>
                  {r.reference && <div className="text-[10.5px] mono-num text-brand-muted truncate max-w-[220px]">{r.reference}</div>}
                </td>
                <td className="mono-num text-[12px] text-brand-muted">{r.expires_at?.slice(0, 10)}</td>
                <td><StatusPill s={r.status} /></td>
                <td>
                  {r.blocking ? (
                    <span className="chip chip-coral mono-num text-[10px]"><ShieldAlert size={11}/> Blocking</span>
                  ) : (
                    <span className="chip mono-num text-[10px]"><ShieldCheck size={11}/> Advisory</span>
                  )}
                </td>
                {canEdit && (
                  <td className="text-right">
                    <button className="btn-ghost !h-7 !w-7 !p-0"
                            onClick={(e) => { e.stopPropagation(); del(r.id); }}
                            data-testid={`${testidPrefix}-delete-${r.id}`}>
                      <Trash2 size={12}/>
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {empty && (
              <tr><td colSpan={canEdit ? 6 : 5} className="p-0">
                <EmptyState icon={Clock} eyebrow="Records"
                  title={filter ? `No ${filter.replace("_", " ")} records.` : "No records yet."}
                  body="Add licences, certificates, and insurance so lapsed cover doesn't quietly slip past a shift."
                  testid={`${testidPrefix}-empty`} />
              </td></tr>
            )}
            {rows === null && (
              <tr><td colSpan={canEdit ? 6 : 5} className="p-4 text-center text-brand-muted text-[12px]">Loading…</td></tr>
            )}
          </tbody>
        </table></div>
      </div>

      <DetailDrawer open={!!drawer} onOpenChange={(v) => { if (!v) setDrawer(null); }}
                    title={drawer === "new" ? "Add record"
                          : (drawer?.subject_name || "Record")}
                    subtitle={drawer === "new" ? "Track a licence, cert, or insurance"
                             : (drawer?.type_label || "")}
                    testid={`${testidPrefix}-drawer`}>
        {drawer && (
          <RecordForm
            companyId={companyId}
            record={drawer === "new" ? null : drawer}
            initialSubject={scope === "member" && subjectId
              ? { type: "member", id: subjectId } : null}
            members={members}
            sites={sites}
            canEdit={canEdit}
            onDone={() => { setDrawer(null); load(); }}
            testidPrefix={testidPrefix}
          />
        )}
      </DetailDrawer>
    </div>
  );
}

function StatChip({ label, value, tone, testid }) {
  const cls = tone === "emerald" ? "text-emerald-700"
            : tone === "amber" ? "text-amber-700"
            : "text-brand-coral";
  return (
    <div className="card-panel px-3 py-2" data-testid={testid}>
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 mono-num text-[16px] font-bold ${cls}`}>{value}</div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = { valid: "chip-emerald", expiring_soon: "chip-amber", lapsed: "chip-coral" };
  const label = { valid: "valid", expiring_soon: "expiring", lapsed: "lapsed" }[s] || s;
  return <span className={`chip ${map[s] || 'chip'} mono-num text-[10px]`}>{label}</span>;
}

function RecordForm({
  companyId, record, initialSubject, members, sites, canEdit, onDone, testidPrefix,
}) {
  const isEdit = !!record;
  const [f, setF] = React.useState(() => ({
    subject_type: record?.subject_type || initialSubject?.type || "member",
    subject_id:   record?.subject_id   || initialSubject?.id   || "",
    type_label:   record?.type_label   || "",
    reference:    record?.reference    || "",
    issued_at:    record?.issued_at    || "",
    expires_at:   record?.expires_at?.slice(0, 10) || "",
    warn_days:    record?.warn_days ?? 30,
    blocking:     record?.blocking ?? false,
    notes:        record?.notes || "",
  }));
  const [busy, setBusy] = React.useState(false);

  async function save() {
    if (busy) return;
    if (!f.type_label.trim() || !f.expires_at || !f.subject_id) {
      toast.error("Fill subject, type, and expires_at.");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/companies/${companyId}/records/${record.id}`, {
          type_label: f.type_label.trim(),
          reference: f.reference,
          issued_at: f.issued_at || null,
          expires_at: f.expires_at,
          warn_days: Number(f.warn_days),
          blocking: !!f.blocking,
          notes: f.notes,
        });
        toast.success("Saved");
      } else {
        await api.post(`/companies/${companyId}/records`, {
          subject_type: f.subject_type,
          subject_id: f.subject_id,
          type_label: f.type_label.trim(),
          reference: f.reference,
          issued_at: f.issued_at || null,
          expires_at: f.expires_at,
          warn_days: Number(f.warn_days),
          blocking: !!f.blocking,
          notes: f.notes,
        });
        toast.success("Added");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  const subjectLocked = isEdit || !!initialSubject;

  return (
    <div className="p-5 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="micro-label">Subject type</label>
          <select className="field-input mt-1" value={f.subject_type}
                  disabled={subjectLocked}
                  onChange={(e) => setF((x) => ({ ...x, subject_type: e.target.value, subject_id: "" }))}
                  data-testid={`${testidPrefix}-subject-type`}>
            <option value="member">Member</option>
            <option value="site">Site</option>
          </select>
        </div>
        <div>
          <label className="micro-label">Subject</label>
          <select className="field-input mt-1" value={f.subject_id}
                  disabled={subjectLocked}
                  onChange={(e) => setF((x) => ({ ...x, subject_id: e.target.value }))}
                  data-testid={`${testidPrefix}-subject-id`}>
            <option value="">—</option>
            {f.subject_type === "member"
              ? members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)
              : sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)
            }
          </select>
        </div>
      </div>
      <div>
        <label className="micro-label">Type / label</label>
        <input className="field-input mt-1" value={f.type_label}
               onChange={(e) => setF((x) => ({ ...x, type_label: e.target.value }))}
               placeholder="e.g. SIA Licence"
               disabled={!canEdit}
               data-testid={`${testidPrefix}-type-label`} />
      </div>
      <div>
        <label className="micro-label">Reference</label>
        <input className="field-input mt-1 mono-num" value={f.reference}
               onChange={(e) => setF((x) => ({ ...x, reference: e.target.value }))}
               placeholder="e.g. licence number"
               disabled={!canEdit}
               data-testid={`${testidPrefix}-reference`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="micro-label">Issued</label>
          <input type="date" className="field-input mt-1 mono-num" value={f.issued_at?.slice(0, 10) || ""}
                 onChange={(e) => setF((x) => ({ ...x, issued_at: e.target.value }))}
                 disabled={!canEdit}
                 data-testid={`${testidPrefix}-issued-at`} />
        </div>
        <div>
          <label className="micro-label">Expires</label>
          <input type="date" className="field-input mt-1 mono-num" value={f.expires_at}
                 onChange={(e) => setF((x) => ({ ...x, expires_at: e.target.value }))}
                 disabled={!canEdit}
                 data-testid={`${testidPrefix}-expires-at`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="micro-label">Warn days</label>
          <input type="number" min="0" max="365"
                 className="field-input mt-1 mono-num" value={f.warn_days}
                 onChange={(e) => setF((x) => ({ ...x, warn_days: e.target.value }))}
                 disabled={!canEdit}
                 data-testid={`${testidPrefix}-warn-days`} />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 cursor-pointer text-[12.5px]">
            <input type="checkbox" checked={f.blocking}
                   onChange={(e) => setF((x) => ({ ...x, blocking: e.target.checked }))}
                   disabled={!canEdit}
                   data-testid={`${testidPrefix}-blocking`} />
            Blocking (halts shift assign if lapsed)
          </label>
        </div>
      </div>
      <div>
        <label className="micro-label">Notes</label>
        <textarea className="field-input mt-1 !h-20" value={f.notes}
                  onChange={(e) => setF((x) => ({ ...x, notes: e.target.value }))}
                  disabled={!canEdit}
                  data-testid={`${testidPrefix}-notes`} />
      </div>
      {canEdit && (
        <div className="pt-3 border-t border-brand-rule">
          <button className="btn-violet" onClick={save} disabled={busy}
                  data-testid={`${testidPrefix}-save`}>
            <Save size={13}/> {busy ? "…" : (isEdit ? "Save" : "Add")}
          </button>
        </div>
      )}
    </div>
  );
}
